import { Component, Input, OnInit, DoCheck, ViewChild, ViewChildren, OnDestroy } from '@angular/core'
import { Resources } from '../../../models/resources'
import { Course } from '../../../models/course'
import { PopoverController } from '@ionic/angular'
import { TeacherProvider } from '../../../providers/teacher/teacher.provider'
import { Router } from '@angular/router'
import { Lesson } from '../../../models/lesson'
import { deserialize } from 'class-transformer'
import { MenuPopoverComponent } from '../../../components/menu-popover/menu-popover.component'
import { LessonSong } from '../../../models/lesson-song'
import { Song } from '../../../models/song'
import { Teacher } from '../../../models/teacher'

import { Common } from '../../../lib/common'
import { CourseCssService } from '../course-css.service'
import { DragulaService } from 'ng2-dragula'
import { v4 as uuid } from 'uuid'
import { Subscription } from 'rxjs'


interface NewLesson {
  screen: number
  name: string
  numSongs: number,
  classType: Course.ClassType
}

@Component({
  selector: 'app-course-lesson-list',
  templateUrl: './course-lesson-list.component.html',
  styleUrls: ['./course-lesson-list.component.scss']
})
export class CourseLessonListComponent implements OnInit, DoCheck, OnDestroy {
  isDragging: boolean = false;
  prevCourse: Course = null
  _course: Course = Course.defaults()
  get course(): Course {
    return this._course
  }

  @Input() set course(course: Course) {
    this._course = course || Course.defaults()
  }

  _resources: Resources = Resources.defaults()
  get resources(): Resources {
    return this._resources
  }

  @Input() set resources(resources: Resources) {
    this._resources = resources || Resources.defaults()
  }

  _teacher: Teacher = Teacher.defaults()
  get teacher(): Teacher {
    return this._teacher
  }

  @Input() set teacher(teacher: Teacher) {
    this._teacher = teacher || Teacher.defaults()
  }

  @Input() isEditing: boolean

  lessons: Lesson[]

  dragGroupName: string

  subs = new Subscription();

  constructor(
    private router: Router,
    private popoverCtrl: PopoverController,
    private teacherProvider: TeacherProvider,
    public courseCssService: CourseCssService,
    private dragulaService: DragulaService
  ) {
    this.dragGroupName = uuid() + 'LESSONS'
    const dragula = this.dragulaService.createGroup(this.dragGroupName, {
      moves: function (el, container, handle) {
        if (Common.canDrag(handle)) {
          el.addEventListener('touchmove', event => event.preventDefault())
          return true
        }
        return false
      },
      accepts: function (el, target, source, sibling) {
        return sibling != null
      }
    })
    dragula.drake.on("drag", () =>{
      this.isDragging = true;
    })
    dragula.drake.on("dragend", () =>{
      this.isDragging = false;
    })
    this.subs.add(dragulaService.drop(this.dragGroupName)
      .subscribe(({ name, el, target, source, sibling }) => {
        this.course.lessons = this.lessons;
        this.course.isDirty = true;
        /* 
          calling below line of code is hitting Update API multiple time(size of lesson array times) 
          and after this entire system doesn't update any thing until angular server is restarted on
          different port.
        */
        // this.course.lessons.forEach((ls: Lesson) => ls.isDirty = true);
        this.lessons = this.course.displayableLessons;
        this.teacherProvider.updateCourse(this.course);
      })
    )
  }

  ngOnInit() {
    this.lessons = this.course.displayableLessons
    let moveList = document.querySelectorAll('.drag-icon')

    if (moveList) {
      moveList.forEach(move => {
        move.addEventListener('touchmove', event => event.preventDefault())
      })
    }

    moveList = document.querySelectorAll('.draggable')
    if (moveList) {
      moveList.forEach(move => {
        move.addEventListener('touchmove', event => event.preventDefault())
      })
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    const group = this.dragulaService.find(this.dragGroupName)
    if (group !== undefined) {
      this.dragulaService.destroy(this.dragGroupName);
    }
  }

  ngDoCheck(): void {
    if (!this.course.equals(this.prevCourse)) {
      this.lessons = this.course.displayableLessons
      if (!Common.isNullOrUndefined(this.newLesson)) {
        const numberOfLessons = this.course.getLessonCounts
        const minLessons = Math.min(...numberOfLessons)
        const maxLessons = Math.max(...numberOfLessons)
        if (this.newLesson.numSongs > maxLessons) {
          this.newLesson.numSongs = maxLessons
        } else if (this.newLesson.numSongs < minLessons) {
          this.newLesson.numSongs = minLessons
        }
      }
      this.prevCourse = this.course
    }
  }

  importLesson(event) {
    // see: https://academind.com/learn/angular/snippets/angular-image-upload-made-easy/#select-a-file
    // https://stackoverflow.com/questions/41155325/how-to-read-a-text-file-saved-on-my-computer-using-javascriptconsole.log(event)
    const file: File = event.target.files[0]
    const reader = new FileReader
    reader.addEventListener('load', () => {
      const lessonJson: string = reader.result as string
      const lesson: Lesson = deserialize(Lesson, lessonJson).unsavedCopy() // unsavedCopy() to prevent people from injecting uuids
      lesson.name = this.course.uniqueLessonName(lesson.name)
      this.teacherProvider.addLesson(this.course, lesson)
    })
    reader.readAsText(file, 'UTF-8')
  }


  // New Lesson

  newLesson: NewLesson

  addNewLessonPhase1() {
    this.newLesson = {
      screen: 1,
      name: '',
      numSongs: this.course.getLessonCounts[0],
      classType: this.course.classType
    }
    if (this.newLesson.classType == Course.ClassType.RhythmKids) {
      this.newLesson.classType = Course.ClassType.RhythmKidsLevel1
    }
  }

  addNewLessonPhase2() {
    if (this.newLesson.name.length > 0) {
      this.newLesson.screen = 2
    }
  }

  addNewLessonDone() {
    const newLesson: NewLesson = this.newLesson
    this.newLesson = null

    const lesson = Lesson.defaults()
    lesson.name = newLesson.name
    lesson.numSongs = newLesson.numSongs
    lesson.songs = Array(newLesson.numSongs).fill(null).map(_ => LessonSong.defaults())
    lesson.classType = newLesson.classType

    const collection = this.resources.findCollection(this.course.collectionUuid)
    if (collection) {
      const helloSong = collection.songs?.find((song: Song) => song.isHelloSong) ?? null
      if (helloSong) {
        lesson.songs[0] = LessonSong.createFrom(helloSong)
      }

      const goodbyeSong = collection.songs?.find((song: Song) => song.isGoodbyeSong) ?? null
      if (goodbyeSong) {
        lesson.songs[lesson.songs.length - 1] = LessonSong.createFrom(goodbyeSong)
      }
    }

    this.teacherProvider.addLesson(this.course, lesson)
  }

  cancelNewAddLesson() {
    this.newLesson = null
  }

  backNewAddLesson() {
    this.newLesson.screen -= 1
  }

  selectedActivitySongsText() {
    if (this.course.classType == Course.ClassType.RhythmKids) {
      if (this.newLesson.classType == Course.ClassType.RhythmKidsLevel2) {
        return `RK Level 2`
      }
      return `RK Level 1`
    }
    return `${this.newLesson.numSongs} Activities`
  }

  chooseNumberOfLessons(event) {
    const numberOfLessons = this.course.getLessonCounts

    if (this.course.classType == Course.ClassType.RhythmKids) {
      const rkLevels = [1, 2]
      MenuPopoverComponent.present(this.popoverCtrl, 'lesson-plans-popover', event, rkLevels.map(cnt => {
        return {
          name: `RK Level ${cnt}`, action: () => {
            this.newLesson.numSongs = 9
            this.newLesson.classType = (cnt == 2) ? Course.ClassType.RhythmKidsLevel2 : Course.ClassType.RhythmKidsLevel1
          }
        }
      }))
    } else if (numberOfLessons.length > 0) {
      MenuPopoverComponent.present(this.popoverCtrl, 'lesson-plans-popover', event, numberOfLessons.map(cnt => {
        return {
          name: cnt + ' Activities', action: () => {
            this.newLesson.numSongs = cnt
          }
        }
      }))
    }
  }

  trackByLessonUuid(index: number, lesson: Lesson): string {
    return lesson.uuid
  }
}
